import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Input,
  Select,
} from "@/components";
import { CREATE_ACCOUNT_ROLE_OPTIONS } from "../../_constants/accounts.const";
import {
  getPatchAccountErrorMessage,
  usePatchAccountMutation,
  type AdminPatchAccountBody,
} from "../../_hooks/useUpdateAccountMutation";
import type { AccountAdminBillingUser } from "../../_types/account-admin-details.types";
import type { AccountRole } from "../../_types/accounts.types";
import styles from "./EditAccountDrawer.module.scss";

export type AdminEditFieldKey =
  | "firstName"
  | "lastName"
  | "email"
  | "username"
  | "phoneNumber"
  | "country"
  | "role"
  | "accountType"
  | "isActive"
  | "emailVerified"
  | "phoneVerified"
  | "onboardingCompleted"
  | "trialUsed";

const FIELD_TITLE: Record<AdminEditFieldKey, string> = {
  firstName: "Edit first name",
  lastName: "Edit last name",
  email: "Edit email",
  username: "Edit username",
  phoneNumber: "Edit phone",
  country: "Edit country",
  role: "Edit role",
  accountType: "Edit account type",
  isActive: "Account active",
  emailVerified: "Email verified",
  phoneVerified: "Phone verified",
  onboardingCompleted: "Onboarding completed",
  trialUsed: "Trial used",
};

function defaultUsername(u: AccountAdminBillingUser, id: string): string {
  if (u.username && u.username.length >= 3) return u.username;
  return `user_${id.replace(/-/g, "").slice(0, 12)}`;
}

function countryFromUser(u: AccountAdminBillingUser): string {
  if (u.country && /^[A-Za-z]{2}$/.test(u.country.trim())) {
    return u.country.trim().toUpperCase();
  }
  return "US";
}

type EditAccountFieldDrawerProps = {
  accountId: string;
  field: AdminEditFieldKey | null;
  isOpen: boolean;
  onClose: () => void;
  billingUser: AccountAdminBillingUser | null | undefined;
  billingLoaded: boolean;
};

export const EditAccountFieldDrawer = ({
  accountId,
  field,
  isOpen,
  onClose,
  billingUser,
  billingLoaded,
}: EditAccountFieldDrawerProps) => {
  const patchMutation = usePatchAccountMutation();
  const [str, setStr] = useState("");
  const [bool, setBool] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !field || !billingUser) return;
    setSubmitError(null);
    const u = billingUser;
    switch (field) {
      case "firstName":
        setStr(u.firstName ?? "");
        break;
      case "lastName":
        setStr(u.lastName ?? "");
        break;
      case "email":
        setStr(u.email ?? "");
        break;
      case "username":
        setStr(defaultUsername(u, accountId));
        break;
      case "phoneNumber":
        setStr(u.phoneNumber ?? "");
        break;
      case "country":
        setStr(countryFromUser(u));
        break;
      case "role":
        setStr(((u.role as AccountRole) ?? "user") as string);
        break;
      case "accountType":
        setStr(u.accountType === "google" ? "google" : "default");
        break;
      case "isActive":
        setBool(u.isActive !== false);
        break;
      case "emailVerified":
        setBool(u.emailVerified === true);
        break;
      case "phoneVerified":
        setBool(u.phoneVerified === true);
        break;
      case "onboardingCompleted":
        setBool(u.onboardingCompleted === true);
        break;
      case "trialUsed":
        setBool(u.trialUsed === true);
        break;
      default:
        break;
    }
  }, [isOpen, field, billingUser, accountId]);

  const handleClose = () => {
    if (patchMutation.isPending) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!field || !billingUser) return;
    setSubmitError(null);

    const body: AdminPatchAccountBody = {};

    switch (field) {
      case "firstName":
        body.firstName = str.trim();
        break;
      case "lastName":
        body.lastName = str.trim();
        break;
      case "email":
        body.email = str.trim().toLowerCase();
        break;
      case "username":
        body.username = str.trim();
        break;
      case "phoneNumber":
        body.phoneNumber = str.trim();
        break;
      case "country": {
        const c = str.trim().toUpperCase();
        if (c.length !== 2) {
          setSubmitError("Country must be a 2-letter code (e.g. US, PL).");
          return;
        }
        body.country = c;
        break;
      }
      case "role":
        body.role = str as AccountRole;
        break;
      case "accountType":
        body.accountType = str as "default" | "google";
        break;
      case "isActive":
        body.isActive = bool;
        break;
      case "emailVerified":
        body.emailVerified = bool;
        break;
      case "phoneVerified":
        body.phoneVerified = bool;
        break;
      case "onboardingCompleted":
        body.onboardingCompleted = bool;
        break;
      case "trialUsed":
        body.trialUsed = bool;
        break;
      default:
        return;
    }

    try {
      await patchMutation.mutateAsync({ accountId, body });
      onClose();
    } catch (err) {
      setSubmitError(getPatchAccountErrorMessage(err));
    }
  };

  const showWait = isOpen && (!billingLoaded || !billingUser);
  const drawerOpen = isOpen && field != null;

  if (!field) {
    return null;
  }

  const title = FIELD_TITLE[field];
  const isBool =
    field === "isActive" ||
    field === "emailVerified" ||
    field === "phoneVerified" ||
    field === "onboardingCompleted" ||
    field === "trialUsed";

  return (
    <Drawer stacked isOpen={drawerOpen} onClose={handleClose} ariaLabel={title}>
      <DrawerHeader title={title} onClose={handleClose} closeDisabled={patchMutation.isPending} />
      {showWait ? (
        <DrawerBody>
          <div className={styles["comp__state"]}>
            <div className={styles["comp__spinner"]} />
            <span>Loading account…</span>
          </div>
        </DrawerBody>
      ) : (
        <form className={styles["comp__stack"]} onSubmit={handleSubmit}>
          <DrawerBody>
            <div className={styles["comp"]}>
              <div className={styles["comp__fields"]}>
                {field === "firstName" ? (
                  <Input
                    id="edit-field-first-name"
                    label="First name"
                    value={str}
                    disabled={patchMutation.isPending}
                    onChange={setStr}
                  />
                ) : null}
                {field === "lastName" ? (
                  <Input
                    id="edit-field-last-name"
                    label="Last name"
                    value={str}
                    disabled={patchMutation.isPending}
                    onChange={setStr}
                  />
                ) : null}
                {field === "email" ? (
                  <Input
                    id="edit-field-email"
                    label="Email"
                    type="text"
                    value={str}
                    disabled={patchMutation.isPending}
                    onChange={setStr}
                  />
                ) : null}
                {field === "username" ? (
                  <Input
                    id="edit-field-username"
                    label="Username"
                    value={str}
                    disabled={patchMutation.isPending}
                    onChange={setStr}
                  />
                ) : null}
                {field === "phoneNumber" ? (
                  <Input
                    id="edit-field-phone"
                    label="Phone"
                    value={str}
                    placeholder="Leave empty to clear"
                    disabled={patchMutation.isPending}
                    onChange={setStr}
                  />
                ) : null}
                {field === "country" ? (
                  <Input
                    id="edit-field-country"
                    label="Country (ISO-2)"
                    value={str}
                    placeholder="US"
                    disabled={patchMutation.isPending}
                    onChange={(v) => setStr(v.toUpperCase().slice(0, 2))}
                  />
                ) : null}
                {field === "role" ? (
                  <Select
                    id="edit-field-role"
                    label="Role"
                    fullWidth
                    size="medium"
                    variant="secondary"
                    value={str}
                    disabled={patchMutation.isPending}
                    onChange={(value) => setStr(value)}
                  >
                    {CREATE_ACCOUNT_ROLE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                ) : null}
                {field === "accountType" ? (
                  <Select
                    id="edit-field-account-type"
                    label="Account type"
                    fullWidth
                    size="medium"
                    variant="secondary"
                    value={str}
                    disabled={patchMutation.isPending}
                    onChange={(value) => setStr(value)}
                  >
                    <option value="default">Default</option>
                    <option value="google">Google</option>
                  </Select>
                ) : null}
                {isBool ? (
                  <Checkbox
                    id={`edit-field-${field}`}
                    variant="primary"
                    checked={bool}
                    disabled={patchMutation.isPending}
                    onChange={setBool}
                  >
                    <Checkbox.Label>Apply this value</Checkbox.Label>
                  </Checkbox>
                ) : null}
              </div>
              {submitError ? <div className={styles["comp__error"]}>{submitError}</div> : null}
            </div>
          </DrawerBody>
          <DrawerFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={patchMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={patchMutation.isPending}>
              Save
            </Button>
          </DrawerFooter>
        </form>
      )}
    </Drawer>
  );
};
